import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { ArrowLeft, Save, Upload, Trash2, Plus, CheckCircle2, XCircle } from "lucide-react";

const LIBS = [
  { key: "pre_construction_library", label: "Pre-Construction Items (Developer responsibilities)" },
  { key: "inclusions_library", label: "Inclusions" },
  { key: "exclusions_library", label: "Exclusions" },
  { key: "assumptions_library", label: "Allowances & Assumptions" },
];

const ProposalSettingsPage = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pingState, setPingState] = useState({ status: "unknown", reply: "" });
  const [newLibItem, setNewLibItem] = useState({});

  const load = async () => {
    try {
      const [s, p] = await Promise.all([
        axios.get(`${API}/proposal-settings`),
        axios.get(`${API}/proposals/ai/ping`),
      ]);
      setSettings(s.data);
      setPingState({ status: p.data.ok ? "ok" : "fail", reply: p.data.ok ? p.data.model : p.data.error });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load settings");
    }
  };
  useEffect(() => { load(); }, []);

  const updateField = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        default_contractor_name: settings.default_contractor_name,
        default_contractor_title: settings.default_contractor_title,
        default_company_name: settings.default_company_name,
        default_insurance_text: settings.default_insurance_text,
        default_valid_days: settings.default_valid_days,
        brand_color: settings.brand_color,
      };
      const resp = await axios.put(`${API}/proposal-settings`, payload);
      setSettings(resp.data);
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const handleUpload = async (kind, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const resp = await axios.post(`${API}/proposal-settings/upload/${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${kind} uploaded`);
      setSettings((s) => ({ ...s, [kind === "logo" ? "logo_url" : "signature_image_url"]: resp.data.url }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    }
  };

  const addLibraryItem = async (libKey) => {
    const text = (newLibItem[libKey] || "").trim();
    if (!text) return;
    try {
      await axios.post(`${API}/proposal-settings/library/${libKey}`, { text, default_checked: true });
      setNewLibItem((s) => ({ ...s, [libKey]: "" }));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Add failed");
    }
  };

  const removeLibraryItem = async (libKey, itemId) => {
    try {
      await axios.delete(`${API}/proposal-settings/library/${libKey}/${itemId}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  if (!settings) {
    return <div className="min-h-screen flex items-center justify-center text-zinc-400">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="proposal-settings-page">
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate("/proposals")} data-testid="back-to-proposals-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Proposals
          </Button>
          <h1 className="font-heading text-2xl font-bold uppercase tracking-tight text-[#00341d]">
            Proposal Settings
          </h1>
          <div className="ml-auto flex items-center gap-3">
            <div className="text-xs flex items-center gap-1.5" data-testid="claude-ping-status">
              {pingState.status === "ok" ? (
                <><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Claude connected ({pingState.reply})</>
              ) : pingState.status === "fail" ? (
                <><XCircle className="w-4 h-4 text-red-600" /> Claude: {pingState.reply}</>
              ) : "Checking..."}
            </div>
            <Button onClick={handleSave} disabled={saving} className="bg-[#00341d] hover:bg-[#002816] text-white" data-testid="save-settings-btn">
              <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        <Card>
          <CardHeader><CardTitle>Brand Assets</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <AssetUploader
                label="Company Logo"
                current={settings.logo_url}
                onUpload={(f) => handleUpload("logo", f)}
                testid="upload-logo"
              />
              <AssetUploader
                label="Signature Image"
                current={settings.signature_image_url}
                onUpload={(f) => handleUpload("signature", f)}
                testid="upload-signature"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Contractor Defaults</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Your Name</Label>
              <Input value={settings.default_contractor_name || ""} onChange={(e) => updateField("default_contractor_name", e.target.value)} data-testid="default-name-input" />
            </div>
            <div>
              <Label>Your Title</Label>
              <Input value={settings.default_contractor_title || ""} onChange={(e) => updateField("default_contractor_title", e.target.value)} data-testid="default-title-input" />
            </div>
            <div>
              <Label>Company Name</Label>
              <Input value={settings.default_company_name || ""} onChange={(e) => updateField("default_company_name", e.target.value)} />
            </div>
            <div>
              <Label>Default Valid Days</Label>
              <Input type="number" value={settings.default_valid_days || 30} onChange={(e) => updateField("default_valid_days", parseInt(e.target.value) || 30)} />
            </div>
            <div className="sm:col-span-2">
              <Label>Default Insurance Language</Label>
              <Textarea rows={3} value={settings.default_insurance_text || ""} onChange={(e) => updateField("default_insurance_text", e.target.value)} data-testid="default-insurance-input" />
            </div>
          </CardContent>
        </Card>

        {LIBS.map((lib) => (
          <Card key={lib.key} data-testid={`library-${lib.key}`}>
            <CardHeader><CardTitle className="text-base">{lib.label}</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2 mb-3">
                {(settings[lib.key] || []).length === 0 && (
                  <p className="text-xs text-zinc-400">No items yet. Added items appear as reusable checkboxes on every new proposal.</p>
                )}
                {(settings[lib.key] || []).map((item) => (
                  <div key={item.id} className="flex items-center gap-2 p-2 bg-zinc-50 rounded border border-zinc-200">
                    <span className="flex-1 text-sm">{item.text}</span>
                    <Button size="sm" variant="ghost" onClick={() => removeLibraryItem(lib.key, item.id)} className="text-red-500 hover:text-red-700">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="Add new item..."
                  value={newLibItem[lib.key] || ""}
                  onChange={(e) => setNewLibItem((s) => ({ ...s, [lib.key]: e.target.value }))}
                  onKeyDown={(e) => e.key === "Enter" && addLibraryItem(lib.key)}
                  data-testid={`add-${lib.key}-input`}
                />
                <Button variant="outline" onClick={() => addLibraryItem(lib.key)} data-testid={`add-${lib.key}-btn`}>
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </main>
    </div>
  );
};

const AssetUploader = ({ label, current, onUpload, testid }) => (
  <div>
    <Label>{label}</Label>
    {current ? (
      <div className="mt-2 border border-zinc-200 rounded bg-white p-3 flex items-center justify-center min-h-[96px]">
        <img
          src={(process.env.REACT_APP_BACKEND_URL || "") + current}
          alt={label}
          className="max-h-24 max-w-full object-contain"
        />
      </div>
    ) : (
      <div className="mt-2 border border-dashed border-zinc-300 rounded bg-zinc-50 p-4 text-center text-sm text-zinc-400 min-h-[96px] flex items-center justify-center">
        No {label.toLowerCase()} uploaded
      </div>
    )}
    <label className="mt-2 inline-flex items-center gap-2 text-sm text-[#00341d] hover:underline cursor-pointer">
      <Upload className="w-4 h-4" />
      Upload {current ? "new" : "image"}
      <input
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => e.target.files?.[0] && onUpload(e.target.files[0])}
        data-testid={testid}
      />
    </label>
  </div>
);

export default ProposalSettingsPage;
